/**
 * Worked examples of well-formed tools. The form offers these on a fresh draft so a
 * developer can see what "good" looks like — clear names, an agent-facing description,
 * and a fully-described input per field — and edit from there instead of a blank page.
 */
import type { HttpToolDefinitionInput } from '../../lib/api';
import {
  BLANK_TOOL_DRAFT,
  deriveAnnotations,
  rowsToRequestMapping,
  rowsToRequestSchema,
  type ToolInputRow,
} from './tool-inputs';

export interface ToolExample {
  key: string;
  label: string;
  /** One line on what kind of tool this is, shown beneath the button. */
  blurb: string;
  draft: HttpToolDefinitionInput;
}

/** Shared defaults so each example only spells out the fields that make it interesting. */
function exampleDraft(
  overrides: Pick<
    HttpToolDefinitionInput,
    'display_name' | 'description' | 'http_method' | 'path_template'
  > & {
    base_url?: string;
    auth_config?: HttpToolDefinitionInput['auth_config'];
    rows: ToolInputRow[];
  },
): HttpToolDefinitionInput {
  const { rows, base_url, auth_config, ...rest } = overrides;
  return {
    ...BLANK_TOOL_DRAFT,
    ...rest,
    base_url: base_url ?? 'https://api.yourcompany.com/v1',
    auth_config: auth_config ?? BLANK_TOOL_DRAFT.auth_config,
    request_schema: rowsToRequestSchema(rows, rest.path_template),
    request_mapping: rowsToRequestMapping(rows, rest.http_method, rest.path_template),
    annotations: deriveAnnotations(rest.http_method),
  };
}

export const TOOL_EXAMPLES: ToolExample[] = [
  {
    key: 'upsert_customer',
    label: 'upsert_customer',
    blurb: 'Writes data — creates or updates a record (POST).',
    draft: exampleDraft({
      display_name: 'upsert_customer',
      description:
        "Create or update a customer in our system. The customer's phone is filled in automatically — call this whenever the customer shares or changes a detail like their name or email so their record stays current.",
      http_method: 'POST',
      path_template: '/customers',
      rows: [
        {
          name: 'phone',
          type: 'string',
          required: true,
          source: 'participantPhone',
          description: "The customer's phone number in E.164 format, e.g. +15551234567.",
        },
        { name: 'name', type: 'string', required: false, description: "The customer's full name." },
        {
          name: 'email',
          type: 'string',
          required: false,
          description: "The customer's email address.",
        },
      ],
    }),
  },
  {
    key: 'get_loyalty_status',
    label: 'get_loyalty_status',
    blurb: 'Reads data — looks something up (GET).',
    draft: exampleDraft({
      display_name: 'get_loyalty_status',
      description:
        "Look up a customer's loyalty tier and point balance by phone number. Call this before answering questions about rewards.",
      http_method: 'GET',
      path_template: '/customers/loyalty',
      rows: [
        {
          name: 'phone',
          type: 'string',
          required: true,
          description: 'Phone number of the customer to look up, in E.164 format.',
        },
      ],
    }),
  },
];
